/**
 * Shared PoR page layout resolution (workspace default + per-record override).
 */
import { POR_PAGE_REGISTRY, type PorPageKey } from './porPageSettingsService';
import { POR_SECTION_ORDER, type PorSectionType } from '../types/plan-of-record';

export type PorLayoutSource = 'record' | 'workspace' | 'default';

export interface PorPageLayoutRow {
  pageKey: PorPageKey;
  isVisible: boolean;
}

/** Build visibility map: record rows win when present; else workspace; else all visible. */
export function resolveEffectivePorPageVisibility(
  workspaceRows: PorPageLayoutRow[],
  recordRows: PorPageLayoutRow[],
): { visibility: Map<PorPageKey, boolean>; source: PorLayoutSource } {
  const source: PorLayoutSource =
    recordRows.length > 0 ? 'record' : workspaceRows.length > 0 ? 'workspace' : 'default';

  const rows = recordRows.length > 0 ? recordRows : workspaceRows;
  const map = new Map<PorPageKey, boolean>();

  for (const page of POR_PAGE_REGISTRY) {
    const stored = rows.find((r) => r.pageKey === page.key);
    map.set(page.key, stored?.isVisible ?? true);
  }

  return { visibility: map, source };
}

export function visiblePorSectionOrderFromMap(
  visibility: Map<PorPageKey, boolean>,
): PorSectionType[] {
  return POR_SECTION_ORDER.filter((type) => visibility.get(type) !== false);
}

export function defaultPorLandingSegmentFromMap(visibility: Map<PorPageKey, boolean>): string {
  for (const page of POR_PAGE_REGISTRY) {
    if (visibility.get(page.key) !== false) return page.hrefSegment;
  }
  return 'dashboard';
}

export function countVisiblePorPagesFromMap(visibility: Map<PorPageKey, boolean>): number {
  return POR_PAGE_REGISTRY.filter((p) => visibility.get(p.key) !== false).length;
}

export function toApiPageLayout(
  visibility: Map<PorPageKey, boolean>,
  source: PorLayoutSource,
) {
  return {
    source,
    pages: POR_PAGE_REGISTRY.map((page) => ({
      page_key: page.key,
      name: page.name,
      description: page.description,
      group: page.group,
      href_segment: page.hrefSegment,
      is_visible: visibility.get(page.key) !== false,
    })),
  };
}
